import React, { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { apiGet } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { formatCodeName } from '../../generic/utilities/objectStructures/codeNameFormat'
import { isExternalEffective, resolveExternCheckboxGating } from './jobExternalCharacter'
import { managesPreparation, resolvePreparationCheckboxGating } from './jobPreparationCharacter'
import { isAnotherJobTheNightJob, isNightJobEffective, NIGHT_JOB_CONTEXT_OFF, resolveExternGatingWithNightMark, resolveNightCheckboxGating } from './jobNightCharacter'
import { collectFeatureGroupIndexValues, filterAdmissibleIndexValues } from './indexDegradation'
import { applyJobReferenceCascadeIndexValue, collectJobReferenceCascadeItems } from './cascadeApproval'
import CascadeIndexChangeDialog from './dialogs/CascadeIndexChangeDialog'
import ChangeTechnologyDialog from './dialogs/ChangeTechnologyDialog'
import ReturnDateField from './ReturnDateField'
import ReturnDurationField from './ReturnDurationField'
import PeriodicReturnField from './PeriodicReturnField'
import { DASH, Field, SectionTitle, SMALL_ACTION_SX } from './ProcessTreeInspectorParts'

/**
 * ProcessTreeJobForm.jsx
 * The general properties of an operation as the process Tree shows and edits them (PO review of
 * 2026-09-11): the SAME fields the graph's job panel offers when a job box is selected —
 * `NodeGeneralProperties.jsx` plus the identity and the technology the node card itself edits — laid
 * out vertically in the inspector's language instead of the panel's horizontal strip.
 *
 * It is a FORM, not a writer: every change goes out through `onChange(jobReference)` (or the identity
 * and technology callbacks) and the caller decides whether that lands in the reducer at once (the
 * inspector, in edit mode) or in a draft that is only committed on Save (the operation dialog).
 *
 * The gating is the panel's own, from its single owners: `resolveExternCheckboxGating` for the Extern
 * mark, `resolvePreparationCheckboxGating` for Manage preparation, `isExternalEffective` for the
 * operators of a subcontracted job (HU-03/RN25.4), and — story 183 — `resolveNightCheckboxGating` for
 * the night mark (RN7: the very answer the graph's panel gets). Copying a formula here is what a test
 * forbids.
 *
 * The lists behind the selects (rules, fixture references, extended columns, the assets of the
 * technology) are asked for ONLY while the form is editable: a read-only inspector requests nothing
 * (RN7, the lens costs no calls it does not need).
 *
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Node} Node
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').JobReference} JobReference
 */

const RETURN_MODE = { DATE: 'DATE', DURATION: 'DURATION', PERIODIC: 'PERIODIC' }

const asList = (value) => (Array.isArray(value) ? value : [])

const nameOptions = (list) => asList(list).map((entry) => ({ value: entry.name || '', label: entry.name || '' }))

/**
 * The catalogues the selects need, loaded once the form becomes editable and the assets again whenever
 * the technology changes (the graph's panel does exactly this, `NodeGeneralProperties.jsx`). A request
 * that fails leaves an empty list: the field then offers only its current value.
 */
const useJobReferenceCatalogs = (enabled, technologyId) => {
  const [catalogs, setCatalogs] = useState({ rules: [], fixtures: [], extendedColumns: [], devices: [] })

  useEffect(() => {
    if (!enabled) {
      return undefined
    }
    let cancelled = false
    const load = async () => {
      const [rules, fixtures, extendedColumns] = await Promise.all([
        apiGet('rules').catch(() => []),
        apiGet('fixtureReferences/').catch(() => []),
        apiGet('extendedColumn/job_reference').catch(() => [])
      ])
      if (!cancelled) {
        setCatalogs((previous) => ({ ...previous, rules: asList(rules), fixtures: asList(fixtures), extendedColumns: asList(extendedColumns) }))
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled || !technologyId) {
      return undefined
    }
    let cancelled = false
    const load = async () => {
      const devices = await apiGet('devices/' + technologyId).catch(() => [])
      if (!cancelled) {
        setCatalogs((previous) => ({ ...previous, devices: asList(devices) }))
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [enabled, technologyId])

  return catalogs
}

const ProcessTreeJobForm = ({
  idPrefix = 'inspector',
  node = {},
  jobReference = {},
  depth = 0,
  branchIndex = 0,
  branchCount = 0,
  editable = false,
  showCounters = true,
  durationValue = DASH,
  onChange = () => {},
  onChangeNodeName = () => {},
  onChangeNodeCode = () => {},
  onChangeTechnology = () => {},
  progressCalculationTypes = [],
  indexValues = [],
  workflowUseIndex = false,
  cascadeApprovalEnabled = false,
  // Story 186: CONTROLLED feature group revisions; the Index cascade then leaves the groups already at or above the target (RN49)
  featureGroupRevisionControlled = false,
  fabricationOrderOperable = true,
  nightJobContext = NIGHT_JOB_CONTEXT_OFF
}) => {
  const { t_ } = useSmartTranslation()
  const [technologyDialogOpen, setTechnologyDialogOpen] = useState(false)
  const [cascadeDialog, setCascadeDialog] = useState({ open: false, targetIndexValue: {}, items: [] })
  const featureGroupCascadePolicy = { controlled: featureGroupRevisionControlled }
  const catalogs = useJobReferenceCatalogs(editable, jobReference.technology?.id)

  const id = (suffix) => idPrefix + '-' + suffix
  const patch = (changes) => onChange({ ...jobReference, ...changes })
  // A field that is not editable has no `onChange` at all, which is what makes it a div (S83).
  const writer = (fn) => (editable ? fn : null)

  // Story 183 (RN6.6): same night block over Extern as the graph's panel, from the same owner.
  const externGating = resolveExternGatingWithNightMark(
    resolveExternCheckboxGating({ jobReference, editing: editable, fabricationOrderOperable }),
    jobReference,
    nightJobContext
  )
  const preparationGating = resolvePreparationCheckboxGating({ editing: editable, fabricationOrderOperable })
  const nightGating = resolveNightCheckboxGating({
    jobReference,
    editing: editable,
    fabricationOrderOperable,
    anotherNightJob: isAnotherJobTheNightJob(nightJobContext, node.id)
  })
  const external = isExternalEffective(jobReference)
  const operatorsDisabled = external
  const technology = jobReference.technology?.name || ''
  const operators = jobReference.operatorQuantity ?? 0
  const minimumQuantity = jobReference.minimumQuantity || 1
  const returnMode = jobReference.externalReturn?.mode || ''

  const returnModes = [
    { value: RETURN_MODE.DATE, label: t_('Date') },
    { value: RETURN_MODE.DURATION, label: t_('Duration') },
    { value: RETURN_MODE.PERIODIC, label: t_('Periodic') }
  ]

  /**
   * Story 80 (RN10/S36): changing mode discards the other mode's value on purpose, so what is persisted
   * is always one mode with one value. Clearing drops the whole object, which is how "not declared" travels.
   */
  const updateReturnMode = (mode) => {
    // Story 182 (S86): a PERIODIC timing carries its rule, empty until defined; DATE and DURATION keep the
    // exact shape they had.
    const externalReturn = mode === RETURN_MODE.PERIODIC
      ? { mode, date: null, duration: null, periodicRule: null }
      : { mode, date: null, duration: null }
    patch({ externalReturn: mode === '' ? null : externalReturn })
  }

  const indexOptions = (cascadeApprovalEnabled
    ? indexValues
    : filterAdmissibleIndexValues(indexValues, collectFeatureGroupIndexValues(jobReference))
  ).map((index) => ({ value: index.name, label: index.name }))

  /**
   * HU-04/HU-05 (enmienda PO 23/07/2026): with WORKFLOW_CASCADE_APPROVAL the index is not filtered —
   * a value the feature groups do not match opens the cascade confirmation instead, the same one the
   * graph's panel opens.
   */
  const updateIndex = (name) => {
    const indexValue = indexValues.find((index) => index.name === name)
    if (!indexValue) {
      return
    }
    if (cascadeApprovalEnabled) {
      const items = collectJobReferenceCascadeItems(jobReference, indexValue.name, node, featureGroupCascadePolicy)
      if (items.length > 0) {
        setCascadeDialog({ open: true, targetIndexValue: indexValue, items })
        return
      }
    }
    patch({ indexValue })
  }

  const technologyAction = editable
    ? (
      <Box
        component='button'
        type='button'
        data-testid={id('change-technology')}
        disabled={jobReference.hasParts === true}
        title={jobReference.hasParts === true ? t_('The job has already started and its technology cannot be changed') : t_('Change technology')}
        onClick={() => setTechnologyDialogOpen(true)}
        sx={SMALL_ACTION_SX}
      >
        {t_('Change')}
      </Box>
      )
    : null

  const renderExtendedColumn = (extendedColumn) => {
    const value = jobReference[extendedColumn.field] || ''
    if (extendedColumn.dataType === 'LIST') {
      return (
        <Field
          key={extendedColumn.field}
          testId={id('field-extended-' + extendedColumn.field)}
          label={extendedColumn.label}
          value={value}
          kind='select'
          options={asList(extendedColumn.options).map((option) => ({ value: option, label: option }))}
          onChange={writer((next) => patch({ [extendedColumn.field]: next }))}
        />
      )
    }
    return (
      <Field
        key={extendedColumn.field}
        testId={id('field-extended-' + extendedColumn.field)}
        label={extendedColumn.label}
        value={editable ? value : (value || DASH)}
        onChange={writer((next) => patch({ [extendedColumn.field]: next }))}
      />
    )
  }

  const GRID = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px 14px' }

  return (
    <Box data-testid={id('job-form')} data-editable={editable} sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <Box>
        <SectionTitle label={t_('Identity')} />
        <Box sx={GRID}>
          <Field testId={id('field-name')} label={t_('Step name')} value={editable ? (node.name || '') : (node.name || DASH)} full onChange={writer(onChangeNodeName)} />
          <Field testId={id('field-code')} label={t_('Code in the process')} value={editable ? (node.code || '') : (node.code || DASH)} error={editable && !(node.code || '').trim()} onChange={writer(onChangeNodeCode)} />
          <Field testId={id('field-level')} label={t_('Sequence level')} value={String(depth + 1)} />
          {/* RN32: the technology is the node card's business in the graph; here it sits with the rest, behind its own dialog. */}
          <Field testId={id('technology')} label={t_('Technology')} value={technology || DASH} kind='select' action={technologyAction} />
          {showCounters
            ? <Field testId={id('field-operators')} label={t_('Operators')} value={String(operators)} kind='number' disabled={operatorsDisabled} reason={operatorsDisabled ? t_('Extern') : ''} onChange={writer((value) => patch({ operatorQuantity: value }))} />
            : null}
          <Field testId={id('field-quantity')} label={t_('Minimum quantity')} value={String(minimumQuantity)} kind='number' min={1} onChange={writer((value) => patch({ minimumQuantity: value }))} />
          {/* S52/RN24: every block explains its own reason; RN23: blocked is neither hidden nor emptied. */}
          <Field testId={id('field-external')} label={t_('Extern')} value={t_('Extern')} kind='switch' on={external} disabled={!externGating.actionable} reason={externGating.actionable ? '' : t_(externGating.blockReason)} onChange={writer((on) => patch({ external: on }))} />
          <Field testId={id('field-preparation')} label={t_('Manage preparation')} value={t_('Manage preparation')} kind='switch' on={managesPreparation(jobReference)} disabled={!preparationGating.actionable} reason={preparationGating.actionable ? '' : t_(preparationGating.blockReason)} onChange={writer((on) => patch({ managePreparation: on }))} />
          {/* Story 183 (S8, RN7): the night mark, gated by the same owner as the graph's panel. */}
          {nightJobContext.enabled
            ? <Field testId={id('field-night-job')} label={t_('Night job')} value={t_('Night job')} kind='switch' on={isNightJobEffective(jobReference)} disabled={!nightGating.actionable} reason={nightGating.actionable ? '' : t_(nightGating.blockReason)} onChange={writer((on) => patch({ nightJob: on }))} />
            : null}
          {workflowUseIndex
            ? <Field testId={id('field-index')} label={t_('Index')} value={jobReference.indexValue?.name || ''} kind='select' options={indexOptions} onChange={writer(updateIndex)} />
            : null}
          {branchIndex > 0 && branchCount > 0
            ? <Field testId={id('branch')} label={t_('Branch')} value={t_('Branch {{index}} of {{count}}', { index: branchIndex, count: branchCount })} />
            : null}
        </Box>
      </Box>

      {/* Story 80: rendered only for an external job — with EXT off the block does not exist at all (S31). */}
      {external
        ? (
          <Box data-testid={id('return-timing')}>
            <SectionTitle label={t_('Return timing')} />
            <Box sx={GRID}>
              <Field testId={id('field-return-mode')} label={t_('Return mode')} value={editable ? returnMode : (returnModes.find((mode) => mode.value === returnMode)?.label || DASH)} kind='select' clearable options={returnModes} onChange={writer(updateReturnMode)} />
              {returnMode === RETURN_MODE.DATE
                ? (
                  <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                    <ReturnDateField value={jobReference.externalReturn?.date} disabled={!editable} onChange={(date) => patch({ externalReturn: { mode: RETURN_MODE.DATE, date, duration: null } })} />
                  </Box>
                  )
                : null}
              {returnMode === RETURN_MODE.DURATION
                ? (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <ReturnDurationField value={jobReference.externalReturn?.duration ?? ''} disabled={!editable} onChange={(duration) => patch({ externalReturn: { mode: RETURN_MODE.DURATION, date: null, duration } })} />
                  </Box>
                  )
                : null}
              {returnMode === RETURN_MODE.PERIODIC
                ? (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <PeriodicReturnField
                      // S124 (RN43.2): the inspector and the job dialog keep this form mounted across nodes;
                      // the key gives each node its own per-form drafts.
                      key={node?.id ?? jobReference.id}
                      rule={jobReference.externalReturn?.periodicRule ?? null}
                      disabled={!editable}
                      onChange={(periodicRule) => patch({ externalReturn: { mode: RETURN_MODE.PERIODIC, date: null, duration: null, periodicRule } })}
                    />
                  </Box>
                  )
                : null}
            </Box>
          </Box>
          )
        : null}

      <Box>
        <SectionTitle label={t_('Resources and calculation')} />
        <Box sx={GRID}>
          <Field testId={id('field-device')} label={t_('Preference Asset')} value={jobReference.preferenceDevice?.name || (editable ? '' : DASH)} kind='select' clearable options={nameOptions(catalogs.devices)} onChange={writer((name) => patch({ preferenceDevice: catalogs.devices.find((device) => device.name === name) || null }))} />
          <Field testId={id('field-planning-group')} label={t_('Planning group')} value={jobReference.planningGroup || (editable ? '' : DASH)} onChange={writer((value) => patch({ planningGroup: value }))} />
          <Field testId={id('field-progress')} label={t_('Progress calculation')} value={jobReference.progressCalculationTypeDTO?.name || (editable ? '' : DASH)} kind='select' options={nameOptions(progressCalculationTypes)} onChange={writer((name) => patch({ progressCalculationTypeDTO: progressCalculationTypes.find((type) => type.name === name) || jobReference.progressCalculationTypeDTO }))} />
          <Field testId={id('field-rule')} label={t_('Rule')} value={jobReference.rule?.name || (editable ? '' : DASH)} kind='select' clearable options={nameOptions(catalogs.rules)} onChange={writer((name) => patch({ rule: catalogs.rules.find((rule) => rule.name === name) || null }))} />
          <Field testId={id('field-fixture')} label={t_('Fixture reference')} value={jobReference.fixtureReference?.name || (editable ? '' : DASH)} kind='select' clearable options={nameOptions(catalogs.fixtures)} onChange={writer((name) => patch({ fixtureReference: catalogs.fixtures.find((fixture) => fixture.name === name) || null }))} />
          {showCounters ? <Field testId={id('field-time')} label={t_('Duration')} value={durationValue} /> : null}
          <Field testId={id('field-remark')} label={t_('Remark')} value={editable ? (jobReference.remark || '') : (jobReference.remark || DASH)} kind='area' full onChange={writer((value) => patch({ remark: value }))} />
        </Box>
      </Box>

      {catalogs.extendedColumns.length > 0
        ? (
          <Box data-testid={id('extended-columns')}>
            <SectionTitle label={t_('Extended columns')} />
            <Box sx={GRID}>
              {catalogs.extendedColumns.map(renderExtendedColumn)}
            </Box>
          </Box>
          )
        : null}

      {/* The graph's own dialog, so a sensitive technology is refused here exactly as it is there. */}
      <ChangeTechnologyDialog
        open={technologyDialogOpen}
        onClose={() => setTechnologyDialogOpen(false)}
        currentTechnology={jobReference.technology}
        onAccept={onChangeTechnology}
      />
      <CascadeIndexChangeDialog
        open={cascadeDialog.open}
        subjectLabel={t_('Job') + ' ' + formatCodeName(node?.code, node?.name)}
        previousIndexValueName={jobReference.indexValue?.name}
        targetIndexValueName={cascadeDialog.targetIndexValue.name}
        items={cascadeDialog.items}
        onAccept={() => {
          onChange(applyJobReferenceCascadeIndexValue(jobReference, cascadeDialog.targetIndexValue, featureGroupCascadePolicy))
          setCascadeDialog({ open: false, targetIndexValue: {}, items: [] })
        }}
        onCancel={() => setCascadeDialog({ open: false, targetIndexValue: {}, items: [] })}
      />
    </Box>
  )
}

export default ProcessTreeJobForm
export { useJobReferenceCatalogs }
